/**
 * @file customer-assignment.tsx
 * @module app/modules/sales-management/sales-manager/customer-assignment/customer-assignment
 * @description Implements the customer assignment interface for sales-management operations.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { useEffect, useState } from "react";
import { Button, Card, Col, FormLabel, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { getKey, setKey } from "../../../../../Global/history";
import {
  listCustomerAssignment,
  listCustomers,
} from "../../../../../utils/storeString";
import { KTSVG } from "../../../../../_admin/helpers";
import { CustomSelect } from "../../../../custom/Select/CustomSelect";
import Method from "../../../../../utils/methods";
import Loader from "../../../../../Global/loader";
import { IMAGES } from "../../../../../utils/assetsAndPermissions";
import {
  buyer,
  customerService,
  customerAssignment,
  assistAdmin,
} from "../../../../../api/apiEndPoints";
import {
  AllCustomers,
  PAGE_LIMIT,
  CustomerAssignment as CustomerAssignmentModule,
  AssistAdmin,
  ActivationManager,
} from "../../../../../utils/constants";
import APICallService from "../../../../../api/apiCallService";
import { customerAssignmentJson } from "../../../../../api/apiJSON/customerAssignment";
import { customerServiceJSON } from "../../../../../api/apiJSON/customerService";
import { success, error } from "../../../../../Global/toast";
import AssignRepModal from "../../../../modals/assign-rep-modal";
import { useAuth } from "../../../auth";
import Pagination from "../../../../../Global/pagination";
import b1 from "../../../../../_admin/assets/media/product/default.svg";
import green from "../../../../../_admin/assets/media/svg_uTrade/green_dot.svg";
import red from "../../../../../_admin/assets/media/svg_uTrade/red_dot.svg";
import { useNavigationHistory } from "../../../../../utils/useNavigationHistory";
import ImageWithFallback from "../../../_common/components/image-with-fallback";

const CustomerAssignment = () => {
  const navigate = useNavigate();
  const { navigateTo } = useNavigationHistory();

  // States for filters and pagination
  const [loading, setLoading] = useState(false);
  const [download, setDownload] = useState(false);
  const [search, setSearch] = useState<any>(
    getKey(listCustomerAssignment.search) || ""
  );
  const [selectedRep, setSelectedRep] = useState<any>(
    getKey(listCustomerAssignment.filterRep) || null
  );
  const [selectedDistrict, setSelectedDistrict] = useState<any>(
    getKey(listCustomerAssignment.filterDistrict) || null
  );
  const [lastOrderDays, setLastOrderDays] = useState<any>(
    getKey(listCustomerAssignment.filterLastOrder) || null
  );
  const [selectedStatus, setSelectedStatus] = useState<any>(
    getKey(listCustomerAssignment.status) !== null && getKey(listCustomerAssignment.status) !== undefined
      ? getKey(listCustomerAssignment.status)
      : undefined
  );
  const [page, setPage] = useState<number>(
    Number(getKey(listCustomerAssignment.page)) || 1
  );
  const [pageLimit, setPageLimit] = useState<number>(
    Number(getKey(listCustomerAssignment.limit)) || PAGE_LIMIT
  );
  const [totalRecords, setTotalRecords] = useState(0);
  const [customers, setCustomers] = useState<any[]>([]);
  const { currentUser } = useAuth();

  // Modal states
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [pendingAssignment, setPendingAssignment] = useState<any>(null);
  const [assignLoading, setAssignLoading] = useState(false);
  const [roleTypeWarning, setRoleTypeWarning] = useState<string | null>(null);

  // Bulk Assign states
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);

  const [districtOptions, setDistrictOptions] = useState<any[]>([]);
  const [repOptions, setRepOptions] = useState<any[]>([]);
  const [repPage, setRepPage] = useState<number>(1);
  const [totalReps, setTotalReps] = useState<number>(0);
  const [repSearch, setRepSearch] = useState<string>("");
  const [repLoading, setRepLoading] = useState<boolean>(false);

  const salesLastOrderOptions = [
    { value: "10", label: "10 Days", title: "10 Days" },
    { value: "20", label: "20 Days", title: "20 Days" },
    { value: "30", label: "30 Days", title: "30 Days" },
  ];

  const activationLastOrderOptions = [
    { value: "30", label: "30+ Days", title: "30+ Days" },
    { value: "zeroRevenue", label: "Zero Revenue Customers", title: "Zero Revenue Customers" },
  ];

  const lastOrderOptions = currentUser?.role.some((obj: any) => obj.type === ActivationManager) ? activationLastOrderOptions : salesLastOrderOptions

  const statusOptions = [
    {
      value: "active",
      label: (
        <>
          <img src={green} width={12} height={12} className="me-3" alt="" />
          <span className="fs-16 fw-600 text-black">Active</span>
        </>
      ),
      title: "Active",
    },
    {
      value: "inactive",
      label: (
        <>
          <img src={red} width={12} height={12} className="me-3" alt="" />
          <span className="fs-16 fw-600 text-black">Inactive</span>
        </>
      ),
      title: "Inactive",
    },
  ];

  useEffect(() => {
    (async () => {
      if (currentUser?.districts?.length > 0) {
        await fetchCustomerAssignmentList(
          page,
          pageLimit,
          search,
          selectedRep,
          selectedDistrict,
          lastOrderDays,
          selectedStatus
        );
        await fetchReps(1, PAGE_LIMIT, "");
      };
    })();
  }, [currentUser?.districts]);
  useEffect(() => {
    (async () => {
      const mappedDistricts = currentUser?.districts?.map((d: any) => ({
        value: d.reference,
        label: d.name || d.title,
        title: d.name || d.title,
      }));
      setDistrictOptions(mappedDistricts);
    })();
  }, [currentUser]);

  const fetchReps = async (
    pageNo: number,
    limit: number,
    searchTerm: string
  ) => {
    setRepLoading(true);
    let params: any = {
      pageNo: pageNo,
      limit: limit,
      searchTerm: searchTerm,
      sortKey: "name",
      sortOrder: 1,
      needCount: true,
      "role[0]": 2, // Sales Rep
      "role[1]": 7, // Activation Rep
      manager: currentUser?._id,
    };

    let apiService = new APICallService(
      assistAdmin.list,
      params,
      "",
      "",
      "",
      "",
      CustomerAssignmentModule
    );
    let response = await apiService.callAPI();
    if (response && response.records) {
      setTotalReps(response.total);
      const mappedOptions = response.records.map((r: any) => ({
        value: r._id,
        label: r.name,
        title: r.name,
        roleType: r.role[0]?.type,
        ...r,
      }));

      setRepOptions((prev) =>
        pageNo === 1 ? mappedOptions : [...prev, ...mappedOptions]
      );
    }
    setRepLoading(false);
  };

  const onRepMenuScrollToBottom = async () => {
    if (repOptions.length < totalReps) {
      const nextPage = repPage + 1;
      setRepPage(nextPage);
      await fetchReps(nextPage, PAGE_LIMIT, repSearch);
    }
  };

  const onRepMenuOpen = async () => {
    // If we want to reset on open every time, or just ensure data is there.
    // Usually standard behavior is to keep current state unless specifically requested.
    // But recieved-direct-goods resets to page 1.
    setRepPage(1);
    // setRepSearch(""); // Optional: clear search on open
    await fetchReps(1, PAGE_LIMIT, repSearch);
  };

  const onRepMenuClose = () => {
    setRepSearch("");
    // Optionally reset list to initial state without search, but might be annoying
  };

  const handleRepInputChange = async (value: string) => {
    setRepSearch(value);
    setRepPage(1);
    await fetchReps(1, PAGE_LIMIT, value);
  };

  const fetchCustomerAssignmentList = async (
    pageNo: number,
    limit: number,
    searchTerm: string,
    repId?: any,
    districtId?: any,
    days?: any,
    status?: any
  ) => {
    setLoading(true);

    let params: any = {
      pageNo: pageNo,
      limit: limit,
      sortKey: "createdAt",
      sortOrder: -1,
      searchTerm: searchTerm,
      needCount: true,
      state: 1,
    };
    if (repId) {
      params["assignedTo[0]"] = repId;
    }
    if (districtId && districtId !== "") {
      params["district[0]"] = districtId;
    } else {
      currentUser?.districts &&
        currentUser?.districts.length &&
        currentUser?.districts.forEach((d: any, i: number) => {
          params[`district[${i}]`] = d?.reference;
        });
    }
    if (days) {
      params.daysSinceLastOrder = days;
    }
    if (status === "active") {
      params.blockState = false;
    } else if (status === "inactive") {
      params.blockState = true;
    }

    const apiService = new APICallService(
      buyer.listOnBoardedBuyers,
      params,
      "",
      "",
      "",
      "",
      AllCustomers
    );

    const response = await apiService.callAPI();
    console.log(response, "response");
    if (response && response.records) {
      const mappedCustomers = response.records.map((c: any) => ({
        _id: c._id,
        name: c.name || (c.business && c.business.name) || "-",
        phone: `${c.phoneCountry} ${c.phone}` || "-",
        ordersPlaced:
          c.analytics && c.analytics?.map((val: any) => val.totalOrders).reduce((a: number, b: number) => a + b, 0) || 0,
        totalSales:
          c.analytics && c.analytics?.map((val: any) => val.totalOrderValue).reduce((a: number, b: number) => a + b, 0) || 0,
        status: c.blocked ? "Inactive" : "Active",
        assignedRep:
          c.assignedRep ||
          (c.representative && c.representative.assignedRep) ||
          null,
        assignedTo: c.assignedTo,
        image: (c.business && c.business.image) || b1,
      }));
      setCustomers(mappedCustomers);
      setTotalRecords(response.total || 0);
    } else {
      // Static data logic or empty fallback
      setCustomers([]);
      setTotalRecords(0);
    }
    setLoading(false);
  };

  const checkRoleTypeWarning = (rep: any, customerIds: string | string[]): string | null => {
    const ids = Array.isArray(customerIds) ? customerIds : [customerIds];
    if (!rep) return null;
    const mismatchedCustomers: string[] = [];
    let existingRoleLabel = "";
    let newRoleLabel = "";
    for (const customerId of ids) {
      const customer = customers.find((c) => c._id === customerId);
      if (customer?.assignedTo) {
        const existingRepRoleType = customer?.assignedTo?.reference?.role[0]?.type;
        const newRepRoleType = rep.roleType;
        if (existingRepRoleType && newRepRoleType && existingRepRoleType !== newRepRoleType) {
          mismatchedCustomers.push(customer.name);
          existingRoleLabel = existingRepRoleType === AssistAdmin ? "Sales Rep" : "Activation Rep";
          newRoleLabel = newRepRoleType === AssistAdmin ? "Sales Rep" : "Activation Rep";
        }
      }
    }
    if (mismatchedCustomers.length === 0) return null;
    const customerList = mismatchedCustomers.map((name) => `"${name}"`).join(", ");
    return `${customerList} ${mismatchedCustomers.length === 1 ? "is" : "are"} currently assigned to a rep with a different role (${existingRoleLabel}). Assigning to the selected rep (${newRoleLabel}) will replace the existing assignment.`;
  };

  const handleAssignRep = (customerId: string, rep: any) => {
    const customer = customers.find((c) => c._id === customerId);
    const warning = checkRoleTypeWarning(rep, customerId);
    setPendingAssignment({
      customerId,
      rep,
      customerName: customer?.name || "Selected Customer",
      isBulk: false,
    });
    setRoleTypeWarning(warning);
    setShowAssignModal(true);
  };

  const handleModalRepChange = (rep: any) => {
    setPendingAssignment((prev: any) => ({ ...prev, rep }));
    if (!pendingAssignment) return;
    const warning = checkRoleTypeWarning(rep, pendingAssignment.customerId);
    setRoleTypeWarning(warning);
  };

  const confirmAssignment = async () => {
    if (!pendingAssignment) return;

    setAssignLoading(true);
    setRoleTypeWarning(null);

    const payload: any = {
      users: Array.isArray(pendingAssignment.customerId)
        ? pendingAssignment.customerId
        : [pendingAssignment.customerId],
      assignedTo: pendingAssignment.rep.value,
    };
    const apiService = new APICallService(
      customerAssignment.assign,
      customerAssignmentJson.assign(payload),
      "",
      "",
      "",
      "",
      CustomerAssignmentModule
    );

    const response = await apiService.callAPI();

    if (response && !response.error) {
      setIsBulkMode(false);
      setSelectedCustomers([]);
      success("Representative assigned successfully");
      await fetchCustomerAssignmentList(
        page,
        pageLimit,
        search,
        selectedRep,
        selectedDistrict,
        lastOrderDays
      );
      setShowAssignModal(false);
      setPendingAssignment(null);
    }
    setAssignLoading(false);
  };

  const handleStatusFilter = async (value: any) => {
    setSelectedStatus(value);
    setKey(listCustomerAssignment.status, value, false);
    setPage(1);
    setKey(listCustomerAssignment.page, 1);
    await fetchCustomerAssignmentList(
      1,
      pageLimit,
      search,
      selectedRep,
      selectedDistrict,
      lastOrderDays,
      value
    );
  };

  const handleSearch = async (value: any) => {
    setSearch(value);
    setPage(1);
    setKey(listCustomerAssignment.search, value, false);
    setKey(listCustomerAssignment.page, 1);
    await fetchCustomerAssignmentList(
      1,
      pageLimit,
      value,
      selectedRep,
      selectedDistrict,
      lastOrderDays,
      selectedStatus
    );
  };

  const handleDownloadExcel = async () => {
    setDownload(true);

    let params: any = {
      pageNo: 1,
      limit: 0,
      sortKey: "createdAt",
      sortOrder: -1,
      searchTerm: search,
      needCount: false,
      download: true,
      utcOffset: new Date().getTimezoneOffset(),
      state: 1,
    };

    if (selectedRep) {
      params["assignedTo[0]"] = selectedRep;
    }
    if (selectedDistrict && selectedDistrict !== "") {
      params["district[0]"] = selectedDistrict;
    } else {
      currentUser?.districts &&
        currentUser?.districts.length &&
        currentUser?.districts.forEach((d: any, i: number) => {
          params[`district[${i}]`] = d?.reference;
        });
    }
    if (lastOrderDays) {
      params.daysSinceLastOrder = lastOrderDays;
    }
    if (selectedStatus === "active") {
      params.blockState = false;
    } else if (selectedStatus === "inactive") {
      params.blockState = true;
    }

    const apiService = new APICallService(
      buyer.listOnBoardedBuyers,
      params,
      "",
      "arraybuffer",
      "",
      "",
      AllCustomers
    );

    const response = await apiService.callAPI();

    if (response) {
      const blob = new Blob([response], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "customer-assignment.xlsx";
      link.click();
      URL.revokeObjectURL(url);
    } else {
      error("Data Not Found");
    }

    setDownload(false);
  };

  const handleRepFilter = async (value: any) => {
    setSelectedRep(value);
    setKey(listCustomerAssignment.filterRep, value, false);
    setPage(1);
    setKey(listCustomerAssignment.page, 1);
    await fetchCustomerAssignmentList(
      1,
      pageLimit,
      search,
      value,
      selectedDistrict,
      lastOrderDays,
      selectedStatus
    );
  };

  const handleDistrictFilter = async (value: any) => {
    console.log(value, "value");
    setSelectedDistrict(value);
    setKey(listCustomerAssignment.filterDistrict, value, false);
    setPage(1);
    setKey(listCustomerAssignment.page, 1);
    await fetchCustomerAssignmentList(
      1,
      pageLimit,
      search,
      selectedRep,
      value,
      lastOrderDays,
      selectedStatus
    );
  };

  const handleLastOrderFilter = async (value: any) => {
    setLastOrderDays(value);
    setKey(listCustomerAssignment.filterLastOrder, value, false);
    setPage(1);
    setKey(listCustomerAssignment.page, 1);
    await fetchCustomerAssignmentList(
      1,
      pageLimit,
      search,
      selectedRep,
      selectedDistrict,
      value,
      selectedStatus
    );
  };

  const handleCurrentPage = async (val: number) => {
    setPage(val);
    setKey(listCustomerAssignment.page, val);
    await fetchCustomerAssignmentList(
      val,
      pageLimit,
      search,
      selectedRep,
      selectedDistrict,
      lastOrderDays,
      selectedStatus
    );
  };

  const handlePageLimit = async (event: any) => {
    const limit = parseInt(event.target.value);
    setPage(1);
    setPageLimit(limit);
    setKey(listCustomerAssignment.page, 1);
    setKey(listCustomerAssignment.limit, limit);
    await fetchCustomerAssignmentList(
      1,
      limit,
      search,
      selectedRep,
      selectedDistrict,
      lastOrderDays,
      selectedStatus
    );
  };

  const handleBulkToggle = () => {
    setIsBulkMode(!isBulkMode);
    setSelectedCustomers([]);
  };

  const handleCheckboxChange = (customerId: string) => {
    setSelectedCustomers((prev) =>
      prev.includes(customerId)
        ? prev.filter((id) => id !== customerId)
        : [...prev, customerId]
    );
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const eligibleIds = customers
        .filter((c) => !c.assignedRep && !c.assignedTo)
        .map((c) => c._id);
      setSelectedCustomers(eligibleIds);
    } else {
      setSelectedCustomers([]);
    }
  };

  const handleConfirmBulk = () => {
    if (selectedCustomers.length === 0) {
      error("Please select at least one customer");
      return;
    }
    // For now just show a toast or open a modal
    // Since we need to pick a rep for bulk, maybe we need another modal
    // But for now let's just log and show success if user wants
    setShowAssignModal(true);
    setPendingAssignment({
      customerId: selectedCustomers,
      rep: null,
      isBulk: true,
    });
    setRoleTypeWarning(null);
  };

  const handleCustomerProfile = (id: string) => {
    let currentScrollY = window.scrollY;
    localStorage.setItem("CustomerId", id);
    setKey(listCustomers.scrollPosition, currentScrollY);
    setKey(listCustomers.search, search, false);
    // setKey(listCustomers.filterCustomerState, customerState, false);
    navigateTo("/customers/customer-profile", { state: id });
  };

  if (currentUser?.districts && currentUser.districts.length === 0) {
    return (
      <div className="p-5">
        <h1 className="fs-22 fw-bolder mb-7">Customer Assignment</h1>
        <div className="card shadow-sm">
          <div className="card-body p-15 d-flex flex-column align-items-center text-center">
            <div className="symbol symbol-100px mb-5">
              <KTSVG
                path="/media/icons/duotune/general/gen044.svg"
                className="svg-icon-5x svg-icon-warning"
              />
            </div>
            <h2 className="fs-22 fw-bolder text-dark mb-4">No Districts Assigned</h2>
            <p className="fs-16 text-gray-600 mw-450px mx-auto mb-0">
              The manager have not assigned any district.
              Please contact your administrator to get access to customer data.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5">
      <Row className="align-items-center mb-7 g-5">
        <Col xs>
          <div className="d-flex justify-content-between">
            <h1 className="fs-22 fw-bolder">Customer Assignment</h1>
            <div className="d-flex gap-4">
              {isBulkMode ? (
                <>
                  <Button
                    variant="outline-secondary"
                    className="uw-btn uw-btn-light uw-btn-lg fs-16 fw-bold btn-lg px-7"
                    onClick={handleBulkToggle}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    className="uw-btn uw-btn-primary uw-btn-lg fs-16 fw-bold btn-lg px-7"
                    onClick={handleConfirmBulk}
                    disabled={selectedCustomers.length === 0}
                  >
                    Assign to {selectedCustomers.length} Selected
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="primary"
                    className="uw-btn uw-btn-primary uw-btn-lg fs-16 fw-bold btn-lg px-7"
                    onClick={handleDownloadExcel}
                    disabled={download}
                  >
                    {!download && (
                      <span className="indicator-label fs-16 fw-bold">
                        Download Excel
                      </span>
                    )}
                    {download && (
                      <span
                        className="indicator-progress fs-16 fw-bold"
                        style={{ display: "block" }}
                      >
                        Please wait...
                        <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                      </span>
                    )}
                  </Button>
                  <Button
                    variant="primary"
                    className="uw-btn uw-btn-primary uw-btn-lg fs-16 fw-bold btn-lg px-7"
                    onClick={handleBulkToggle}
                  >
                    Bulk Assign
                  </Button>
                </>
              )}
            </div>
          </div>

          <Card className="bg-light border my-7">
            <Card.Body className="px-7">
              <Row className="align-items-center gy-5">
                <Col sm={12} md={6} lg={4}>
                  <FormLabel className="fs-16 fw-500 text-dark">
                    Search by customer name and number
                  </FormLabel>
                  <div className="d-flex align-items-center position-relative">
                    <KTSVG
                      path="/media/icons/duotune/general/gen021.svg"
                      className="svg-icon-3 position-absolute ms-3"
                    />
                    <input
                      type="text"
                      className="form-control form-control-custom borde-r8px bg-white ps-11"
                      placeholder="Search by customer name and number"
                      value={search}
                      onChange={(event: any) => {
                        handleSearch(event.target.value);
                      }}
                    />
                  </div>
                </Col>
                <Col sm={12} md={6} lg={4}>
                  <FormLabel className="fs-16 fw-500 text-dark">
                    Filter by Assigned Rep
                  </FormLabel>
                  <div>
                    <CustomSelect
                      backgroundColor="#ffff"
                      options={repOptions}
                      value={repOptions.find((o) => o.value == selectedRep)}
                      onChange={(e: any) =>
                        handleRepFilter(e ? e.value : undefined)
                      }
                      isSearchable={true}
                      isMulti={false}
                      isClearable={true}
                      placeholder="Select"
                      isLoading={repLoading}
                      onMenuScrollToBottom={onRepMenuScrollToBottom}
                      onMenuOpen={onRepMenuOpen}
                      onMenuClose={onRepMenuClose}
                      onInputChange={(newValue: any, { action }: any) => {
                        if (action === "input-change") {
                          handleRepInputChange(newValue);
                        }
                      }}
                    />
                  </div>
                </Col>
                <Col sm={12} md={6} lg={4}>
                  <FormLabel className="fs-16 fw-500 text-dark">
                    Filter by district
                  </FormLabel>
                  <div>
                    <CustomSelect
                      backgroundColor="#ffff"
                      options={districtOptions}
                      value={districtOptions?.find(
                        (o) => o.value == selectedDistrict
                      )}
                      onChange={(e: any) =>
                        handleDistrictFilter(e ? e.value : undefined)
                      }
                      isSearchable={false}
                      isMulti={false}
                      isClearable={true}
                      placeholder="Select District"
                    />
                  </div>
                </Col>
                <Col sm={12} md={6} lg={4}>
                  <FormLabel className="fs-16 fw-500 text-dark">
                    Customer Not Order in
                  </FormLabel>
                  <div>
                    <CustomSelect
                      backgroundColor="#ffff"
                      options={lastOrderOptions}
                      value={lastOrderOptions.find(o => o.value == lastOrderDays)}
                      onChange={(e: any) =>
                        handleLastOrderFilter(e ? e.value : undefined)
                      }
                      isSearchable={false}
                      isMulti={false}
                      isClearable={true}
                      placeholder="Select days"
                    />
                  </div>
                </Col>
                <Col sm={12} md={6} lg={4}>
                  <FormLabel className="fs-16 fw-500 text-dark">
                    Filter by Status
                  </FormLabel>
                  <div>
                    <CustomSelect
                      backgroundColor="#ffff"
                      options={statusOptions}
                      value={statusOptions.find(o => o.value == selectedStatus) || null}
                      onChange={(e: any) =>
                        handleStatusFilter(e ? e.value : undefined)
                      }
                      isSearchable={false}
                      isMulti={false}
                      isClearable={true}
                      placeholder="Select Status"
                    />
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {!loading ? (
            customers && customers.length ? (
              <>
                <Card className="border border-r10px">
                  <Card.Body className="p-0">
                    <div className="table-responsive">
                      <table className="table table-rounded table-row-bordered align-middle gs-7 gy-6 mb-0">
                        <thead>
                          <tr className="fw-bold fs-16 fw-600 text-dark border-bottom align-middle">
                            {isBulkMode && (
                              <th className="w-50px">
                                {/* <div className="form-check form-check-sm form-check-custom form-check-solid">
                                  <input
                                    className="form-check-input h-30px w-30px"
                                    type="checkbox"
                                    checked={
                                      selectedCustomers.length > 0 && 
                                      selectedCustomers.length === customers.filter(c => !c.assignedRep).length
                                    }
                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                  />
                                </div> */}
                              </th>
                            )}
                            <th className="min-w-250px">Customer Name</th>
                            {!lastOrderDays && (
                              <th className="min-w-150px">No. of Order Placed</th>
                            )}
                            <th className="min-w-150px">Total Sales</th>
                            <th className="min-w-125px">Status</th>
                            <th className="min-w-200px">Assigned Rep</th>
                            <th className="min-w-100px text-end"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {customers.map((customer, index) => (
                            <tr key={customer._id}>
                              {isBulkMode && (
                                <td>
                                  <div className="form-check form-check-sm form-check-custom form-check-solid">
                                    <input
                                      className="form-check-input h-30px w-30px"
                                      type="checkbox"
                                      disabled={
                                        !!customer.assignedTo &&
                                        repOptions.some(
                                          (rep) => rep.value === (customer.assignedTo?.reference?._id || customer.assignedTo?._id || customer.assignedTo)
                                        )
                                      }
                                      checked={
                                        (!!customer.assignedTo &&
                                          repOptions.some(
                                            (rep) => rep.value === (customer.assignedTo?.reference?._id || customer.assignedTo?._id || customer.assignedTo)
                                          )) ||
                                        selectedCustomers.includes(customer._id)
                                      }
                                      onChange={() =>
                                        handleCheckboxChange(customer._id)
                                      }
                                    />
                                  </div>
                                </td>
                              )}
                              <td>
                                <div className="d-flex align-items-center">
                                  <div className="symbol symbol-45px me-5">
                                    <ImageWithFallback
                                      className="img-fluid border-r8px object-fit-contain"
                                      src={customer.image}
                                      alt=""
                                      style={{ borderRadius: "8px" }}
                                      noDefaultSize
                                    />
                                  </div>
                                  <div className="d-flex justify-content-start flex-column">
                                    <span className="text-dark fw-bolder fs-16 mb-1">
                                      {customer.name}
                                    </span>
                                    <span className="text-gray fw-500 fs-15 d-block">
                                      {customer.phone}
                                    </span>
                                  </div>
                                </div>
                              </td>
                              {!lastOrderDays && (
                                <td>
                                  <span className="fs-15 fw-500 text-dark">
                                    {customer.ordersPlaced
                                      .toString()
                                      .padStart(2, "0")}
                                  </span>
                                </td>
                              )}
                              <td>
                                <span className="fs-15 fw-500 text-dark">
                                  Tsh{" "}
                                  {Method.getGeneralizedAmount(
                                    customer.totalSales
                                  )}
                                </span>
                              </td>
                              <td>
                                <span
                                  className={`badge py-2 px-4 fs-14 rounded-pill ${customer.status === "Active"
                                    ? "bg-light-success text-success"
                                    : "bg-light-danger text-danger"
                                    }`}
                                >
                                  {customer.status}
                                </span>
                              </td>
                              <td>
                                <div style={{ width: "180px" }}>
                                  <CustomSelect
                                    key={
                                      customer._id +
                                      (customer.assignedRep || "none") +
                                      (pendingAssignment?.customerId ===
                                        customer._id
                                        ? "-pending"
                                        : "")
                                    }
                                    options={repOptions}
                                    value={
                                      repOptions.find(
                                        (o) =>
                                          o.value ===
                                          (customer?.assignedTo?.reference?._id || "")
                                      ) || null
                                    }
                                    placeholder="Assign"
                                    isSearchable={true}
                                    backgroundColor="#ffff"
                                    minHeight="45px"
                                    controlFontSize="15px"
                                    disabled={isBulkMode}
                                    onChange={(e: any) =>
                                      handleAssignRep(customer._id, e)
                                    }
                                    isLoading={repLoading}
                                    onMenuScrollToBottom={
                                      onRepMenuScrollToBottom
                                    }
                                    onMenuOpen={onRepMenuOpen}
                                    onMenuClose={onRepMenuClose}
                                    onInputChange={(
                                      newValue: any,
                                      { action }: any
                                    ) => {
                                      if (action === "input-change") {
                                        handleRepInputChange(newValue);
                                      }
                                    }}
                                  />
                                </div>
                              </td>
                              <td className="text-end">
                                <Button
                                  className="uw-btn uw-btn-primary fs-14 fw-600 ms-4"
                                  variant="primary"
                                  onClick={() => {
                                    handleCustomerProfile(customer._id);
                                  }}
                                >
                                  View details
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card.Body>
                </Card>

                <div className="mt-7">
                  <Pagination
                    totalRecords={totalRecords}
                    currentPage={page}
                    handleCurrentPage={handleCurrentPage}
                    handleNextPage={() => handleCurrentPage(page + 1)}
                    handlePreviousPage={() => handleCurrentPage(page - 1)}
                    handlePageLimit={handlePageLimit}
                    pageLimit={pageLimit}
                  />
                </div>
              </>
            ) : (
              <div className="border border-r10px mb-6">
                <div className="d-flex justify-content-center text-center align-items-center min-h-160px px-3">
                  <span className="fs-18 fw-500">No Customers Found</span>
                </div>
              </div>
            )
          ) : (
            <div className="border border-r10px mb-6">
              <div className="d-flex justify-content-center text-center align-items-center min-h-160px px-3">
                <Loader loading={loading} />
              </div>
            </div>
          )}
        </Col>
      </Row>

      <AssignRepModal
        show={showAssignModal}
        onHide={() => {
          setShowAssignModal(false);
          setPendingAssignment(null);
          setRoleTypeWarning(null);
        }}
        onConfirm={confirmAssignment}
        pendingAssignment={pendingAssignment}
        loading={assignLoading}
        roleTypeWarning={roleTypeWarning}
      >
        <CustomSelect
          options={repOptions}
          value={pendingAssignment?.rep}
          onChange={(e: any) => handleModalRepChange(e)}
          placeholder="Select Representative"
          isSearchable={true}
          backgroundColor="#F8F9FA"
          radiusTable="8px"
          minHeight="55px"
          border="none"
          isLoading={repLoading}
          onMenuScrollToBottom={onRepMenuScrollToBottom}
          onMenuOpen={onRepMenuOpen}
          onMenuClose={onRepMenuClose}
          onInputChange={(newValue: any, { action }: any) => {
            if (action === "input-change") {
              handleRepInputChange(newValue);
            }
          }}
        />
      </AssignRepModal>
    </div>
  );
};

export default CustomerAssignment;
